/**
 * Recibos térmicos en PDF (jsPDF + autotable), mismo formato que los recibos
 * de bankivo (imprimirpagos.js): ticket de 77mm en helvetica, etiquetas en
 * negrita, tabla de cuotas y totales alineados a la derecha.
 *
 * El PDF se carga en un iframe oculto y se lanza print() para que la impresión
 * salga automática tras cobrar: un window.open después del await queda fuera
 * del gesto del usuario y el navegador lo bloquea como popup.
 */
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

// Largo por defecto del ticket (77mm de ancho x este alto).
const ALTO_TICKET = 355;
// Espacio por defecto entre el pie del recibo y la línea de firma.
const ESPACIO_FIRMA = 10;
// Lado del QR al pie, en mm: legible en térmicas de 203 dpi sin alargar el ticket.
const LADO_QR = 28;
const X_ETIQUETA = 4;
const X_VALOR = 24;
const X_TOTAL_ETIQ = 6;
const X_TOTAL_VALOR = 72;

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

function separador(doc, y) {
  doc.setFontSize(5);
  doc.text("____________________________________________________________________________", 1, y);
  doc.setFontSize(8);
}

/**
 * Genera e imprime un recibo térmico.
 *
 * @param {Object} cfg
 * @param {string} cfg.titulo            Título del recibo (p.ej. "Recibo de Pago").
 * @param {Object|string} cfg.compania   {nombre, direccion, rnc, telefono} o el nombre solo.
 * @param {Array}  cfg.campos            Pares [etiqueta, valor] del encabezado.
 * @param {Object} [cfg.tabla]           {head: [..], body: [[..]]} tabla central opcional.
 * @param {Array}  [cfg.totales]         {etiqueta, valor, destacado} — valor se formatea a moneda.
 * @param {Array}  [cfg.notas]           Líneas de nota al pie.
 * @param {string} [cfg.firma]           Texto bajo la línea de firma ("Firma", "Preparado Por"...).
 * @param {number} [cfg.alto]            Largo del ticket en mm (por defecto 355).
 * @param {number} [cfg.espacioFirma]    Espacio en mm entre el pie y la línea de firma.
 * @param {Object} [cfg.qr]              {dataUrl, mensaje} QR al pie con su leyenda (lib/portalqr).
 */
export function imprimirReciboPdf({
  titulo,
  compania,
  campos = [],
  tabla,
  totales = [],
  notas = [],
  firma,
  alto = ALTO_TICKET,
  espacioFirma = ESPACIO_FIRMA,
  qr,
}) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: [77, alto] });
  const xc = doc.internal.pageSize.width / 2;
  const comp = typeof compania === "string" ? { nombre: compania } : compania || {};

  // Encabezado de la compañía
  let y = 12;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(String(comp.nombre || ""), xc, y, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  [comp.direccion, comp.rnc ? `RNC: ${comp.rnc}` : "", comp.telefono].filter(Boolean).forEach((linea) => {
    y += 4;
    doc.text(String(linea), xc, y, { align: "center" });
  });

  y += 6;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(String(titulo || ""), xc, y, { align: "center" });

  y += 3;
  separador(doc, y);

  // Campos etiqueta/valor
  y += 5;
  doc.setFontSize(8);
  campos
    .filter((c) => c && c[1] != null && String(c[1]) !== "")
    .forEach(([etiqueta, valor]) => {
      doc.setFont("helvetica", "bold");
      doc.text(String(etiqueta), X_ETIQUETA, y);
      doc.setFont("helvetica", "normal");
      // Valores largos (nombres) se parten para no salirse del ticket.
      const lineas = doc.splitTextToSize(String(valor), 77 - X_VALOR - 2);
      doc.text(lineas, X_VALOR, y);
      y += 4 * lineas.length;
    });

  // Tabla central (cuotas, conceptos...)
  let finalY = y;
  if (tabla && tabla.body && tabla.body.length) {
    y += 2;
    autoTable(doc, {
      head: [tabla.head],
      body: tabla.body,
      startY: y,
      margin: { left: 0, right: 0 },
      // Térmica: sin rellenos. Un fondo de color sale como una trama de
      // puntos y el texto blanco de encima no se lee; la cabecera va en
      // negrita negra con una raya debajo.
      theme: "plain",
      styles: { fontSize: 8, halign: "center", cellPadding: 1, textColor: 0 },
      headStyles: { fontStyle: "bold", halign: "center", textColor: 0 },
      didDrawCell: (d) => {
        if (d.section !== "head") return;
        d.doc.setLineWidth(0.3);
        d.doc.setDrawColor(0);
        const yLinea = d.cell.y + d.cell.height;
        d.doc.line(d.cell.x, yLinea, d.cell.x + d.cell.width, yLinea);
      },
      didDrawPage: (d) => {
        finalY = d.cursor.y;
      },
    });
    finalY += 4;
  } else {
    finalY += 1;
  }

  // Totales
  if (totales.length) {
    separador(doc, finalY);
    finalY += 5;
    totales
      .filter((t) => t)
      .forEach(({ etiqueta, valor, destacado }) => {
        if (destacado) {
          doc.setFontSize(9);
          doc.setFont("helvetica", "bold");
          doc.text(String(etiqueta), X_TOTAL_ETIQ, finalY);
          doc.text(fmtMoney(valor), X_TOTAL_VALOR, finalY, { align: "right" });
          doc.setFontSize(8);
        } else {
          doc.setFont("helvetica", "bold");
          doc.text(String(etiqueta), X_TOTAL_ETIQ, finalY);
          doc.setFont("helvetica", "normal");
          doc.text(fmtMoney(valor), X_TOTAL_VALOR, finalY, { align: "right" });
        }
        finalY += 4.5;
      });
  }

  // Notas al pie
  if (notas.length) {
    finalY += 1;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.5);
    notas.forEach((nota) => {
      doc.text(String(nota), X_ETIQUETA, finalY);
      finalY += 3.5;
    });
    doc.setFontSize(8);
  }

  // Firma
  if (firma) {
    finalY += espacioFirma;
    doc.setFont("helvetica", "normal");
    doc.text("_______________________", xc, finalY, { align: "center" });
    finalY += 4;
    doc.text(String(firma), xc, finalY, { align: "center" });
  }

  // QR al pie (portal de clientes), centrado bajo la firma
  if (qr && qr.dataUrl) {
    finalY += 6;
    // Un recibo con muchas cuotas puede llegar al final del ticket.
    if (finalY + LADO_QR + 8 > alto) {
      doc.addPage();
      finalY = 8;
    }
    doc.addImage(qr.dataUrl, "PNG", xc - LADO_QR / 2, finalY, LADO_QR, LADO_QR);
    finalY += LADO_QR + 4;
    if (qr.mensaje) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.text(String(qr.mensaje), xc, finalY, { align: "center" });
    }
  }

  imprimirPdf(doc);
}

// Carga el PDF en un iframe oculto y lanza la impresión automática.
function imprimirPdf(doc) {
  const url = doc.output("bloburl");
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
  iframe.src = url;
  iframe.onload = () => {
    setTimeout(() => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (e) {
        // Si el visor de PDF no permite imprimir desde el iframe, se abre en pestaña.
        window.open(url, "_blank");
      }
      // Se remueve después de que el diálogo de impresión se cierre.
      setTimeout(() => {
        iframe.remove();
        URL.revokeObjectURL(url);
      }, 60000);
    }, 250);
  };
  document.body.appendChild(iframe);
}
